/**
 * 빌드 상태 + 실행 버튼 (DESIGN §7.2.1).
 *
 * **왜 상단 바인가.** 사용자가 gold 가 묵은 것을 알아챈 자리가 여기다 —
 * 빌드 버전이 이미 상단 우측에 있었는데, 그것이 "수집이 8/4에 끊겼다"로
 * 읽혔다. 버전만 있고 **나이가 없어서** 생긴 오해다. 그래서 버전 옆에
 * "얼마나 묵었나"를 붙이고, 그 자리에서 바로 고칠 수 있게 한다.
 *
 * 자동 실행은 걸지 않는다 — 사용자가 언제 도는지 통제하기를 택했다.
 * 대신 나이를 상시 표시해 **조용히 묵는 일**이 없게 한다.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { API_BASE } from '@/lib/api'
import './BuildControl.css'

interface BuildStatus {
  running: boolean
  state: string | null
  started_at: string | null
  finished_at: string | null
  note: string | null
  data_version: string | null
  built_at: string | null
  silver_latest: string | null
  /** gold 가 silver 보다 뒤처진 분. 이 값이 없으면 판정 불가다 */
  gold_lag_min: number | null
  /** 이 요청자가 빌드를 시작할 수 있는가. 로컬에서만 true (§7.2.1) */
  can_build?: boolean
}

/** 이만큼 뒤처지면 눈에 띄게 한다. 하루치 수집이 통째로 안 반영된 상태다 */
const STALE_MIN = 24 * 60

function ago(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.round(ms / 60000)
  if (m < 60) return `${m}분`
  if (m < 60 * 48) return `${Math.round(m / 60)}시간`
  return `${Math.round(m / 1440)}일`
}

function lagText(min: number | null): string {
  if (min == null) return '나이 판정 불가'
  if (min < 60) return `최신 (${Math.round(min)}분 차)`
  if (min < 1440) return `${Math.round(min / 60)}시간 묵음`
  return `${Math.round(min / 1440)}일 묵음`
}

export function BuildControl() {
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['admin/build/status'],
    queryFn: async ({ signal }) => {
      const r = await fetch(`${API_BASE}/admin/build/status`, { signal })
      if (!r.ok) throw new Error(String(r.status))
      return (await r.json()) as BuildStatus
    },
    // 도는 동안만 자주 본다 — 평소에 5초마다 두드릴 이유가 없다
    refetchInterval: (query) => (query.state.data?.running ? 5_000 : 60_000),
    staleTime: 0,
  })

  const start = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_BASE}/admin/build`, {
        method: 'POST',
        // 커스텀 헤더가 preflight 를 강제한다 — CSRF 방어 (§7.2.1)
        headers: { 'X-EVCA-Action': 'build' },
      })
      if (!r.ok) {
        const body = (await r.json().catch(() => null)) as { detail?: string } | null
        throw new Error(body?.detail ?? `실패 (${r.status})`)
      }
      return r.json()
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin/build/status'] }),
  })

  const s = q.data
  const running = s?.running ?? false
  const lag = s?.gold_lag_min ?? null
  const stale = lag != null && lag >= STALE_MIN

  return (
    <div className="bctl">
      {running ? (
        <span className="bctl__state bctl__state--run">
          <span className="bctl__dot" aria-hidden="true" />
          빌드 중 · {ago(s?.started_at ?? null)} 경과
        </span>
      ) : (
        <span className={`bctl__state${stale ? ' bctl__state--stale' : ''}`}>
          {lagText(lag)}
        </span>
      )}

      {/* 로컬이 아니면 **버튼을 그리지 않는다** — 눌러도 403 인 버튼은
          "왜 안 되지"를 만들 뿐이다. 상태 문구는 그대로 보인다. */}
      {s?.can_build !== false && (
      <button
        type="button"
        className="bctl__btn"
        onClick={() => start.mutate()}
        disabled={running || start.isPending}
        title={
          running
            ? '이미 빌드가 진행 중입니다'
            : '수집된 최신 데이터로 화면을 다시 만듭니다 (수십 분 걸립니다)'
        }
      >
        {running ? '진행 중…' : '지금 빌드'}
      </button>
      )}

      {start.error && (
        <span className="bctl__err" role="alert">
          {(start.error as Error).message}
        </span>
      )}
      {!running && s?.state === 'failed' && (
        <span className="bctl__err" role="alert">
          지난 빌드 실패
        </span>
      )}
    </div>
  )
}
